import React, { useMemo } from "react";
import { Users, Activity, TrendingUp, Clock, Wallet, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { formatCurrency } from "@/lib/commission";
import { Sparkline } from "./Sparkline";
import {
  currentMonthKey,
  previousMonthKey,
  type InfluencersStats,
  type MonthlyTotals,
} from "./hooks/useInfluencersData";

interface Delta {
  direction: "up" | "down" | "flat";
  text: string;
}

function makeDelta(current: number, previous: number, formatter: (v: number) => string): Delta | null {
  if (previous === 0 && current === 0) return null;
  const diff = current - previous;
  if (Math.abs(diff) < 0.005) return { direction: "flat", text: "estável vs mês anterior" };
  return {
    direction: diff > 0 ? "up" : "down",
    text: `${diff > 0 ? "+" : "−"}${formatter(Math.abs(diff))} vs mês anterior`,
  };
}

interface StatTileProps {
  icon: React.ElementType;
  label: string;
  value: string;
  delta?: Delta | null;
  hint?: string;
  sparkline?: number[];
  loading: boolean;
}

const StatTile: React.FC<StatTileProps> = ({ icon: Icon, label, value, delta, hint, sparkline, loading }) => (
  <div className="rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-4 transition-colors hover:border-white/20">
    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    {loading ? (
      <div className="mt-2 space-y-2">
        <div className="skeleton-shimmer h-7 w-24" />
        <div className="skeleton-shimmer h-3 w-32" />
      </div>
    ) : (
      <>
        <div className="mt-1.5 flex items-end justify-between gap-2">
          <p className="text-xl sm:text-2xl font-semibold tracking-tight">{value}</p>
          {sparkline && sparkline.length >= 2 && (
            <Sparkline values={sparkline} className="shrink-0 mb-0.5 hidden sm:block" />
          )}
        </div>
        {delta ? (
          <p
            className={`mt-1 flex items-center gap-1 text-xs ${
              delta.direction === "up"
                ? "text-green-400"
                : delta.direction === "down"
                  ? "text-red-400"
                  : "text-muted-foreground"
            }`}
          >
            {delta.direction === "up" && <ArrowUpRight className="h-3 w-3" />}
            {delta.direction === "down" && <ArrowDownRight className="h-3 w-3" />}
            {delta.text}
          </p>
        ) : hint ? (
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </>
    )}
  </div>
);

interface InfluencersStatsHeaderProps {
  stats: InfluencersStats;
  monthlyTotals: MonthlyTotals[];
  loading: boolean;
}

export const InfluencersStatsHeader: React.FC<InfluencersStatsHeaderProps> = ({
  stats,
  monthlyTotals,
  loading,
}) => {
  const { salesDelta, activeDelta, commissionsDelta, salesSpark, activeSpark, commissionsSpark } =
    useMemo(() => {
      const cur = monthlyTotals.find((m) => m.month === currentMonthKey());
      const prev = monthlyTotals.find((m) => m.month === previousMonthKey());
      const count = (v: number) => String(Math.round(v));
      return {
        salesDelta: makeDelta(cur?.sales || 0, prev?.sales || 0, formatCurrency),
        activeDelta: makeDelta(cur?.activeCount || 0, prev?.activeCount || 0, count),
        commissionsDelta: makeDelta(cur?.commissions || 0, prev?.commissions || 0, formatCurrency),
        salesSpark: monthlyTotals.map((m) => m.sales),
        activeSpark: monthlyTotals.map((m) => m.activeCount),
        commissionsSpark: monthlyTotals.map((m) => m.commissions),
      };
    }, [monthlyTotals]);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
      <StatTile
        icon={Users}
        label="Influenciadoras"
        value={String(stats.totalInfluencers)}
        hint="Total registradas"
        loading={loading}
      />
      <StatTile
        icon={Activity}
        label="Ativas"
        value={String(stats.activeInfluencers)}
        delta={activeDelta}
        hint="Com vendas recentes"
        sparkline={activeSpark}
        loading={loading}
      />
      <StatTile
        icon={TrendingUp}
        label="Vendas no mês"
        value={formatCurrency(stats.totalSalesThisMonth)}
        delta={salesDelta}
        sparkline={salesSpark}
        loading={loading}
      />
      <StatTile
        icon={Clock}
        label="Saques pendentes"
        value={stats.pendingWithdrawalsAmount === null ? "—" : formatCurrency(stats.pendingWithdrawalsAmount)}
        hint="Aguardando pagamento"
        loading={loading}
      />
      <StatTile
        icon={Wallet}
        label="Comissões geradas"
        value={formatCurrency(stats.totalCommissions)}
        delta={commissionsDelta}
        sparkline={commissionsSpark}
        loading={loading}
      />
    </div>
  );
};

export default InfluencersStatsHeader;
